import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";

// Core components - loaded immediately
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthCallback } from "./components/auth/AuthCallback";

// Home page - loaded immediately (LCP)
import Home from "./pages/Home";

// Lazy-loaded pages - split into separate chunks
const EMRList = lazy(() => import("./pages/EMRList"));
const EMRDetail = lazy(() => import("./pages/EMRDetail"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const VendorDetail = lazy(() => import("./pages/VendorDetail"));
const Compare = lazy(() => import("./pages/Compare"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Wallet = lazy(() => import("./pages/Wallet"));
const WalletInfo = lazy(() => import("./pages/WalletInfo"));
const CustomServices = lazy(() => import("./pages/CustomServices"));
const Pricing = lazy(() => import("./pages/Pricing"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const SelectionGuide = lazy(() => import("./pages/SelectionGuide"));
const SharedGuideResults = lazy(() => import("./pages/SharedGuideResults"));
const ResourcesGuide = lazy(() => import("./pages/ResourcesGuide"));
const DataMigration = lazy(() => import("./pages/DataMigration"));
const HL7Integration = lazy(() => import("./pages/HL7Integration"));
const CloudDeployment = lazy(() => import("./pages/CloudDeployment"));
const PrivacySecurity = lazy(() => import("./pages/PrivacySecurity"));
const ForVendors = lazy(() => import("./pages/ForVendors"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ElevateLab = lazy(() => import("./pages/ElevateLab"));
const SenzCareAI = lazy(() => import("./pages/SenzCareAI"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.baseURL = API;

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="loading-spinner w-8 h-8"></div>
  </div>
);

// Google sign-in returns with #session_id=...; handle it before any route renders.
const AuthHashGate = ({ children }) => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return children;
};

function App() {
  return (
    <div className="App min-h-screen bg-gray-50">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Navbar />
          <main className="pt-20">
            <Suspense fallback={<PageLoader />}>
              <AuthHashGate>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/emrs" element={<EMRList />} />
                <Route path="/emr/:id" element={<EMRDetail />} />
                <Route path="/vendor/:id" element={<VendorDetail />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/for-vendors" element={<ForVendors />} />
                <Route path="/join" element={<Navigate to="/for-vendors" replace />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/ai-solutions" element={<Navigate to="/marketplace?category=ai_automation" replace />} />
                <Route path="/wallet-info" element={<WalletInfo />} />
                <Route path="/custom-services" element={<CustomServices />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/elevate-lab" element={<ElevateLab />} />
                <Route path="/selection-guide" element={<SelectionGuide />} />
                <Route path="/selection-guide/results/:token" element={<SharedGuideResults />} />
                <Route path="/resources-guide" element={<ResourcesGuide />} />
                <Route path="/data-migration" element={<DataMigration />} />
                <Route path="/hl7-integration" element={<HL7Integration />} />
                <Route path="/cloud-deployment" element={<CloudDeployment />} />
                <Route path="/privacy-security" element={<PrivacySecurity />} />
                <Route path="/senzcare-ai" element={<SenzCareAI />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/wallet" 
                  element={
                    <ProtectedRoute>
                      <Wallet />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/complete-profile"
                  element={
                    <ProtectedRoute>
                      <CompleteProfile />
                    </ProtectedRoute>
                  }
                />
              </Routes>
              </AuthHashGate>
            </Suspense>
          </main>
          <Footer />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
